'use client';

import { CheckCircle, Minus, XCircle } from 'lucide-react';

import { BenefitsSection } from '@/components/sections/benefits-section';
import { CustomerVideos } from '@/components/sections/customer-videos';
import { SubpageHero } from '@/components/sections/subpage-hero';
import { TemplatesList } from '@/components/sections/templates-list';
import Testimonials from '@/components/sections/testimonials';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { MagicalText } from '@/components/ui/magical-text';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useTranslation } from '@/lib/i18n/context';
import { cn } from '@/lib/utils';

/* Comparison rows. `true` renders a tick, `false` a cross, null a dash —
   the same three states /helpdesks/hubspot uses for its table. */
const COMPARISON: {
  key: string;
  open: boolean | null;
  legacy: boolean | null;
}[] = [
  { key: 'A new feature from one sentence', open: true, legacy: false },
  { key: 'Ships without an engineer', open: true, legacy: false },
  { key: 'Waits on a vendor roadmap', open: false, legacy: true },
  { key: 'Runs on your live customer context', open: true, legacy: null },
  { key: 'Renders beside the conversation', open: true, legacy: false },
  { key: 'Audit trail and one-click rollback', open: true, legacy: null },
];

const Mark = ({ value }: { value: boolean | null }) =>
  value === true ? (
    <CheckCircle className="h-4 w-4 text-green-600" />
  ) : value === false ? (
    <XCircle className="h-4 w-4 text-red-600" />
  ) : (
    <Minus className="text-muted-foreground h-4 w-4" />
  );

export default function AppBuilderPage() {
  const { t } = useTranslation();

  return (
    <>
      <SubpageHero
        eyebrow={t.features_pages['App Builder']}
        title={t.features_pages["World's first self-building platform"]}
        subtitle={
          t.features_pages[
            "Open is the first CX platform you can extend by prompting. Ask Companion for a feature that doesn't exist yet, and it builds it into the platform — live, in the channel your team already works in."
          ]
        }
        videoSrc="/home/features/app-builder.mp4"
        videoControls
        videoStartAt={60}
        aspectRatio="16/9"
      />

      <CustomerVideos />

      <BenefitsSection
        title={t.features_pages['App Builder']}
        subtitle={t.features_pages['Describe the feature. Open builds it.']}
        learnMoreLink="#comparison"
        accentColor="emerald"
        theme="light"
        benefits={[
          {
            title: t.features_pages['Driver Location In The Channel'],
            description:
              t.features_pages[
                'An agent mid-conversation needs the driver position and ETA, which normally means switching tabs while the customer waits. One prompt builds a live location app into the channel.'
              ],
            linkText: '',
            linkHref: '#',
            videoUrl: '/product/app-builder/driver-location.mp4',
            imagePosition: 'left',
          },
          {
            title: t.features_pages['QA On Your Own Scorecard'],
            description:
              t.features_pages[
                "Every team scores quality differently. Instead of bending to someone else's QA module, describe your scorecard and Companion builds the app around it."
              ],
            linkText: '',
            linkHref: '#',
            videoUrl: '/product/app-builder/qa-tracker.mp4',
            imagePosition: 'right',
          },
          {
            title: t.features_pages['Whatever You Need Next'],
            description:
              t.features_pages[
                'Nothing about the method changes with the ask. A returns triage board, a fraud review queue, a carrier SLA tracker. Describe it and it gets built, whether or not it has ever existed.'
              ],
            linkText: '',
            linkHref: '#',
            videoUrl: '/product/app-builder/prompt-to-app.mp4',
            imagePosition: 'left',
          },
        ]}
      />

      {/* Comparison Section */}
      <section id="comparison" className="bg-white py-32">
        <div className="container mx-auto">
          <div className="mx-auto mb-16 flex max-w-3xl flex-col justify-center gap-7 text-center md:text-center">
            <h2 className="font-serif text-2xl md:text-4xl">
              <MagicalText>{t.features_pages['App Builder']}</MagicalText>{' '}
              {t.features_pages['vs']}{' '}
              {t.features_pages['waiting for the roadmap']}
            </h2>
            <p className="text-muted-foreground text-sm md:text-base">
              {
                t.features_pages[
                  'What changes when the feature you are missing is a sentence away instead of a quarter away.'
                ]
              }
            </p>
          </div>
          <div className="relative overflow-hidden p-8">
            <div className="border-border/50 bg-background/50 relative overflow-hidden border backdrop-blur-sm">
              <Table>
                <TableHeader>
                  <TableRow className="border-border/50">
                    <TableHead className="text-foreground font-semibold">
                      {t.features_pages['Capability']}
                    </TableHead>
                    <TableHead className="text-foreground text-center font-semibold">
                      <MagicalText>
                        {t.features_pages['App Builder']}
                      </MagicalText>
                    </TableHead>
                    <TableHead className="text-foreground text-center font-semibold">
                      {t.features_pages['A traditional CX platform']}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {COMPARISON.map((row, i) => (
                    <TableRow
                      key={row.key}
                      className={cn(
                        'border-border/50',
                        i % 2 === 1 && 'bg-muted/20',
                      )}
                    >
                      <TableCell className="text-foreground py-4 font-medium">
                        {
                          t.features_pages[
                            row.key as keyof typeof t.features_pages
                          ]
                        }
                      </TableCell>
                      <TableCell className="py-4">
                        <div className="flex justify-center">
                          <Mark value={row.open} />
                        </div>
                      </TableCell>
                      <TableCell className="py-4">
                        <div className="flex justify-center">
                          <Mark value={row.legacy} />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </section>

      {/* Templates Section */}
      <section className="bg-neutral-50 py-32">
        <div className="container mx-auto">
          <div className="mx-auto mb-16 flex max-w-3xl flex-col justify-center gap-7 text-center md:text-center">
            <h2 className="font-serif text-2xl md:text-4xl">
              {t.features_pages['Start from a template, then build past it']}
            </h2>
            <p className="text-muted-foreground text-sm md:text-base">
              {
                t.features_pages[
                  'Open ships playbooks for the work every support team shares. The app builder is for everything after that — the parts nobody could have specced in advance.'
                ]
              }
            </p>
          </div>
          <TemplatesList
            categoryFilter="Customer Support"
            limit={6}
            variant="slider"
          />
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="bg-white py-32">
        <div className="container space-y-16">
          <div className="mx-auto flex max-w-3xl flex-col justify-center gap-7 text-center md:text-center">
            <h2 className="font-serif text-2xl md:text-4xl">
              {t.helpdesk['Frequently asked questions']}
            </h2>
            <p className="text-muted-foreground text-sm md:text-base">
              {
                t.features_pages[
                  'Find answers to common questions about the App Builder.'
                ]
              }
            </p>
          </div>
          <Accordion
            type="single"
            collapsible
            className="mx-auto w-full lg:max-w-3xl"
          >
            <AccordionItem value="faq-1">
              <AccordionTrigger className="transition-opacity duration-200 hover:no-underline hover:opacity-60">
                <div className="font-medium sm:py-1 lg:py-2 lg:text-lg">
                  {t.features_pages['What is the Open app builder?']}
                </div>
              </AccordionTrigger>
              <AccordionContent className="sm:mb-1 lg:mb-2">
                <div className="text-muted-foreground lg:text-lg">
                  {
                    t.features_pages[
                      'It lets you extend Open with features that were never in the product. You describe what you need to Companion in one prompt, and it builds a working app into the platform.'
                    ]
                  }
                </div>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="faq-2">
              <AccordionTrigger className="transition-opacity duration-200 hover:no-underline hover:opacity-60">
                <div className="font-medium sm:py-1 lg:py-2 lg:text-lg">
                  {t.features_pages['Do I need a developer?']}
                </div>
              </AccordionTrigger>
              <AccordionContent className="sm:mb-1 lg:mb-2">
                <div className="text-muted-foreground lg:text-lg">
                  {
                    t.features_pages[
                      'No. The input is a sentence, not a spec. Most of these are built by support leads and ops managers describing the thing they keep switching tabs for.'
                    ]
                  }
                </div>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="faq-3">
              <AccordionTrigger className="transition-opacity duration-200 hover:no-underline hover:opacity-60">
                <div className="font-medium sm:py-1 lg:py-2 lg:text-lg">
                  {t.features_pages['What can actually be built?']}
                </div>
              </AccordionTrigger>
              <AccordionContent className="sm:mb-1 lg:mb-2">
                <div className="text-muted-foreground lg:text-lg">
                  {
                    t.features_pages[
                      'Workforce management, QA and real-time monitoring across channels, upselling dashboards, live order and driver tracking, and several thousand smaller things. If it reads your data, your connected tools, or the conversation in front of you, it is buildable.'
                    ]
                  }
                </div>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="faq-4">
              <AccordionTrigger className="transition-opacity duration-200 hover:no-underline hover:opacity-60">
                <div className="font-medium sm:py-1 lg:py-2 lg:text-lg">
                  {t.features_pages['Where do the apps live?']}
                </div>
              </AccordionTrigger>
              <AccordionContent className="sm:mb-1 lg:mb-2">
                <div className="text-muted-foreground lg:text-lg">
                  {
                    t.features_pages[
                      'Inside Open, next to the conversation they serve. A driver-tracking app sits in the delivery channel, a QA app sits where quality is reviewed. Nothing gets its own tab.'
                    ]
                  }
                </div>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="faq-5">
              <AccordionTrigger className="transition-opacity duration-200 hover:no-underline hover:opacity-60">
                <div className="font-medium sm:py-1 lg:py-2 lg:text-lg">
                  {
                    t.features_pages[
                      'Can it change things without us approving?'
                    ]
                  }
                </div>
              </AccordionTrigger>
              <AccordionContent className="sm:mb-1 lg:mb-2">
                <div className="text-muted-foreground lg:text-lg">
                  {
                    t.features_pages[
                      'No. Multi-step work is shown as a plan first, so you approve, edit or stop it before anything runs. Every build leaves an audit trail and anything built can be rolled back.'
                    ]
                  }
                </div>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="faq-6">
              <AccordionTrigger className="transition-opacity duration-200 hover:no-underline hover:opacity-60">
                <div className="font-medium sm:py-1 lg:py-2 lg:text-lg">
                  {
                    t.features_pages[
                      'How is this different from a workflow or an integration?'
                    ]
                  }
                </div>
              </AccordionTrigger>
              <AccordionContent className="sm:mb-1 lg:mb-2">
                <div className="text-muted-foreground lg:text-lg">
                  {
                    t.features_pages[
                      'A workflow automates a process Open already models, and an integration connects a tool that already exists. The app builder adds functionality that did not exist anywhere: new interfaces, new views, new features in the product itself.'
                    ]
                  }
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      <Testimonials />
    </>
  );
}
